/**
 * SWM Color Math Utilities
 * Handles Hex <-> HSL conversions and accessibility logic.
 */

interface HSL {
  h: number;
  s: number;
  l: number;
}

// Convert Hex to RGB
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

// Convert RGB to HSL
export const rgbToHsl = (r: number, g: number, b: number): HSL => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

// Convert Hex directly to HSL object
export const hexToHslObject = (hex: string): HSL => {
  const rgb = hexToRgb(hex);
  if (!rgb) return { h: 0, s: 0, l: 0 }; // Fallback black
  return rgbToHsl(rgb.r, rgb.g, rgb.b);
};

// Get formatted HSL string for Tailwind (e.g. "217 91% 60%")
export const formatHsl = ({ h, s, l }: HSL): string => {
  return `${h} ${s}% ${l}%`;
};

// Calculate luminance to determine if text should be white or black
export const getContrastForeground = (hex: string): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return '0 0% 100%'; // Fallback white

  // Calculate relative luminance
  const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  
  // Threshold: if bright background, return dark text, else white text
  return luminance > 0.5 ? '222.2 47.4% 11.2%' : '0 0% 100%';
};

// Generate a soft tint (lighter for light mode, darker for dark mode handled via CSS ideally, 
// but here we generate a specific "soft" accent color for badges/backgrounds)
export const getSoftTint = (hsl: HSL, mode: 'light' | 'dark' = 'light'): string => {
  if (mode === 'light') {
    // High lightness, lower saturation slightly
    return `${hsl.h} ${Math.max(0, hsl.s - 10)}% 95%`;
  } else {
    // Dark mode tint (darker background)
    return `${hsl.h} ${Math.max(0, hsl.s - 20)}% 15%`;
  }
};
